import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToHorizontalAxis, restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS, Transform } from '@dnd-kit/utilities';
import * as Color from 'core/colors';
import { cloneElement, ReactElement, useEffect, useState } from 'react';
import { IconName, MemreIcon } from './index';

const MemreSortableList = ({
  items,
  render,
  onReorder,
  restrictAxis,
}: {
  items: any[];
  render: (item: any, Handle: any) => ReactElement;
  onReorder: (newOrder: string[], itemId: string) => void;
  restrictAxis?: 'x' | 'y' | undefined;
}) => {
  const initialOrder = items.map((i) => i.id);
  const [itemOrder, setItemOrder] = useState<string[]>(initialOrder);
  const sensors = useSensors(useSensor(PointerSensor));

  const modifiers = (restrictAxis &&
    {
      x: [restrictToHorizontalAxis, restrictToParentElement],
      y: [restrictToVerticalAxis, restrictToParentElement],
    }[restrictAxis]) || [restrictToParentElement];

  useEffect(() => {
    setItemOrder(initialOrder);
  }, [items]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setItemOrder((currentOrder) => {
        const oldIndex = currentOrder.indexOf(active.id as string);
        const newIndex = currentOrder.indexOf(over.id as string);
        const newOrder = arrayMove(currentOrder, oldIndex, newIndex);
        onReorder(newOrder, String(active.id));
        return newOrder;
      });
    }
  };
  return (
    <DndContext modifiers={modifiers} sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={itemOrder} strategy={verticalListSortingStrategy}>
        {itemOrder.map((id) => {
          const item = items.find((i) => i.id === id);
          return item ? <SortableItem key={id} id={id} render={render} item={item} /> : null;
        })}
      </SortableContext>
    </DndContext>
  );
};

const SortableItem = ({ id, render, item }: { id: string; render: any; item: any }) => {
  const { attributes, listeners, setNodeRef, transform: rawTransform, transition, isDragging } = useSortable({ id });

  const newTransform: Transform = {
    x: rawTransform?.x || 0,
    y: rawTransform?.y || 0,
    scaleX: isDragging ? 1.05 : 1,
    scaleY: isDragging ? 1.05 : 1,
  };

  const transform = `${CSS.Transform.toString(newTransform)}${isDragging ? ' rotate(-0.5deg)' : ''}`;
  const style = {
    transform,
    transition,
    touchAction: 'none',
    zIndex: isDragging ? 999 : 'auto',
    boxShadow: isDragging ? '0 4px 8px rgba(0, 0, 0, 0.2)' : 'none',
  };

  return (
    <div ref={setNodeRef} style={style}>
      {render(item, <MemreDragHandle id={item.id} isDragging={isDragging} />)}
    </div>
  );
};

export function MemreDragHandle({ id, isDragging }) {
  const { attributes, listeners } = useSortable({ id });

  return (
    <div {...attributes} {...listeners}>
      <MemreIcon iconName={IconName.grip} color={Color.darkBlue} style={{ cursor: isDragging ? 'grabbing' : 'grab' }} />
    </div>
  );
}

export default MemreSortableList;
